import { Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

import {
  BtrEventModel,
  IAttendeeItemToServer,
  NewAttendeeInformation,
  TicketModel,
} from '@app/core/models';
import { EAttendeeInformation } from '@app/core/models/api';
import { MapTypeEnum } from '../enums/map-type.enum';
import { StorageKeyEnum } from '../enums/storage-key.enum';
import { CuratedCategoryModel } from '../models/curated-category.model';
import { IMapTypeModel } from '../models/map-type.model';
import { PlaceModel } from '../models/place.model';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class EventsService {
  public readonly openMobileMap$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public readonly ticketPurchaseComplete$: BehaviorSubject<boolean>;
  public readonly showMobileMapButton$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(true);
  public readonly openLogin$: BehaviorSubject<boolean>;
  public readonly navigationPlaceCompleat$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public readonly placeSource$: Observable<PlaceModel>;
  public readonly categorySource$: Observable<CuratedCategoryModel>;
  public readonly loadedPins$: Observable<boolean>;
  public readonly sendToTripSource$: Observable<PlaceModel>;
  public readonly errorHappens$: Observable<string>;
  public readonly clearSearch$: Observable<boolean>;
  public readonly isSearchMinimal$: Observable<boolean>;
  public readonly isSlidesRendered$: Observable<boolean>;
  public readonly isMapReady$: Observable<boolean>;
  public readonly closeMobileMenuValue$: Observable<boolean>;
  public readonly signInComplete$: Observable<boolean>;
  public readonly openYourCoupons$: Observable<boolean>;
  public readonly couponPurchaseComplete$: Observable<boolean>;
  public readonly isLoading$: Observable<boolean>;
  public readonly isFirstLoading$: Observable<boolean>;
  public readonly mapType$: Observable<IMapTypeModel>;
  public readonly setUserDone$: BehaviorSubject<boolean>;
  public readonly hideMap$: BehaviorSubject<boolean>;
  public readonly showSignUpDialog$: BehaviorSubject<boolean>;
  public readonly loginInProgress$: BehaviorSubject<boolean>;
  public readonly errorOnLogin$: BehaviorSubject<boolean>;
  public readonly forceLogout$: Observable<string>;

  private category: CuratedCategoryModel = null;
  private readonly categorySource: BehaviorSubject<CuratedCategoryModel>;

  private beaconTrip: PlaceModel = null;
  private readonly sendToTripSource: BehaviorSubject<PlaceModel>;

  private error: string = null;
  private readonly errorHappens: BehaviorSubject<string>;

  private isClear = false;
  private readonly clearSearch: BehaviorSubject<boolean>;

  private isMinimal = false;
  private readonly isSearchMinimal: BehaviorSubject<boolean>;

  private isSlidesRenderedValue = false;
  private readonly isSlidesRendered: BehaviorSubject<boolean>;

  private isMapReadyValue = false;
  private readonly isMapReady: BehaviorSubject<boolean>;

  private readonly beaconSource: Subject<PlaceModel>;
  private readonly loadedPins: Subject<boolean>;
  private readonly closeMobileMenuValue: Subject<boolean>;
  private readonly signInComplete: Subject<boolean>;
  private readonly openYourCoupons: Subject<boolean>;
  private readonly couponPurchaseComplete: BehaviorSubject<boolean>;
  private readonly isLoading: BehaviorSubject<boolean>;
  private readonly isFirstLoading: BehaviorSubject<boolean>;
  private readonly mapType: BehaviorSubject<IMapTypeModel>;
  private readonly forceLogout: BehaviorSubject<string>;

  constructor(
    private storageService: StorageService,
    private translateService: TranslateService,
    private router: Router,
  ) {
    this.beaconSource = new Subject();
    this.placeSource$ = this.beaconSource.asObservable();

    this.categorySource = new BehaviorSubject(this.category);
    this.categorySource$ = this.categorySource.asObservable();

    this.loadedPins = new Subject();
    this.loadedPins$ = this.loadedPins.asObservable();

    this.sendToTripSource = new BehaviorSubject(this.beaconTrip);
    this.sendToTripSource$ = this.sendToTripSource.asObservable();

    this.errorHappens = new BehaviorSubject(this.error);
    this.errorHappens$ = this.errorHappens.asObservable();

    this.forceLogout = new BehaviorSubject(null);
    this.forceLogout$ = this.forceLogout.asObservable();

    this.clearSearch = new BehaviorSubject(this.isClear);
    this.clearSearch$ = this.clearSearch.asObservable();

    this.isSearchMinimal = new BehaviorSubject(this.isMinimal);
    this.isSearchMinimal$ = this.isSearchMinimal.asObservable();

    this.isSlidesRendered = new BehaviorSubject(this.isSlidesRenderedValue);
    this.isSlidesRendered$ = this.isSlidesRendered.asObservable();

    this.isMapReady = new BehaviorSubject(this.isMapReadyValue);
    this.isMapReady$ = this.isMapReady.asObservable();

    this.closeMobileMenuValue = new Subject();
    this.closeMobileMenuValue$ = this.closeMobileMenuValue.asObservable();

    this.signInComplete = new Subject();
    this.signInComplete$ = this.signInComplete.asObservable();

    this.openYourCoupons = new Subject();
    this.openYourCoupons$ = this.openYourCoupons.asObservable();

    this.couponPurchaseComplete = new BehaviorSubject(false);
    this.couponPurchaseComplete$ = this.couponPurchaseComplete.asObservable();

    this.ticketPurchaseComplete$ = new BehaviorSubject(false);
    this.openLogin$ = new BehaviorSubject(false);

    this.isLoading = new BehaviorSubject(false);
    this.isLoading$ = this.isLoading.asObservable();

    this.isFirstLoading = new BehaviorSubject(true);
    this.isFirstLoading$ = this.isFirstLoading.asObservable();

    this.mapType = new BehaviorSubject({ type: MapTypeEnum.DEFAULT });
    this.mapType$ = this.mapType.asObservable();

    this.setUserDone$ = new BehaviorSubject(false);
    this.hideMap$ = new BehaviorSubject(false);
    this.showSignUpDialog$ = new BehaviorSubject(false);
    this.loginInProgress$ = new BehaviorSubject(false);
    this.errorOnLogin$ = new BehaviorSubject(false);
  }

  prepareTicketList(
    attendees: Array<NewAttendeeInformation>,
    tickets: Array<ITicketFormValue>,
  ): Array<IAttendeeItemToServer> {
    let dataForServer = [
      { key: 'ticket-INITIAL_FORMS', value: `0` },
      {
        key: 'ticket-TOTAL_FORMS',
        value: tickets
          .map(item => item.count)
          .reduce((partialSum, a) => partialSum + a, 0)
          .toString(),
      },
    ];
    let ticketIndex = 0;
    tickets.map(ticketItem => {
      for (let i = 0; i < ticketItem.count; i++) {
        dataForServer.push(
          BtrEventModel.formTicketAttendeeToServer(
            ticketItem.ticket,
            ticketIndex,
          ),

          ...attendees
            // eslint-disable-next-line @typescript-eslint/no-loop-func
            .map(item => BtrEventModel.formAttendeeToServer(item, ticketIndex))
            .reduce((acumulator, number) => {
              return acumulator.concat(number);
            }, []),
        );
        ticketIndex += 1;
      }
    });
    // data.map(item => dataForServer.push(...item));
    return dataForServer;
  }

  prepareTicketListFromTicketAttendeeArray(
    tickets: Array<{
      title: string;
      ticket: TicketModel;
      formArray: FormArray<FormGroup>;
    }>,
  ): Array<IAttendeeItemToServer> {
    let dataForServer = [
      { key: 'ticket-INITIAL_FORMS', value: `0` },
      {
        key: 'ticket-TOTAL_FORMS',
        value: tickets.length.toString(),
      },
    ];
    let ticketIndex = 0;

    tickets.map(ticketItem => {
      let attendees = ticketItem.formArray.getRawValue() as any;
      dataForServer.push(
        BtrEventModel.formTicketAttendeeToServer(
          ticketItem.ticket,
          ticketIndex,
        ),
        // eslint-disable-next-line @typescript-eslint/no-loop-func
        ...attendees
          .map(item => BtrEventModel.formAttendeeToServer(item, ticketIndex))
          .reduce((acumulator, number) => {
            return acumulator.concat(number);
          }, []),
      );
      ticketIndex += 1;
    });
    // data.map(item => dataForServer.push(...item));
    return dataForServer;
  }

  sourceBeacon(beacon: PlaceModel): void {
    this.beaconSource.next(beacon);
  }

  sourceCategory(category: CuratedCategoryModel): void {
    this.categorySource.next(category);
  }

  pinsLoaded(loaded: boolean): void {
    this.loadedPins.next(loaded);
  }

  clearSearchSource(clear: boolean): void {
    this.clearSearch.next(clear);
  }

  setSendToTripSource(beacon: PlaceModel): void {
    if (this.storageService.getStorageItem(StorageKeyEnum.USER) != null) {
      this.sendToTripSource.next(beacon);
    } else {
      this.openLogin$.next(true);
    }
  }

  errorHappensSource(error: string, getTextFormTranslate = true): void {
    if (getTextFormTranslate) {
      this.translateService.get(`errors.${error}`).subscribe(value => {
        this.errorHappens.next(value);
        this.storageService.setStorageItem(StorageKeyEnum.IS_ERROR_SHOW, true);
      });
    } else {
      this.errorHappens.next(error);
      this.storageService.setStorageItem(StorageKeyEnum.IS_ERROR_SHOW, true);
    }
  }

  forceLogoutSource(error: string, getTextFormTranslate = true): void {
    if (getTextFormTranslate) {
      this.translateService.get(`errors.${error}`).subscribe(value => {
        if (error !== 'wrong_verification_code') {
          this.forceLogout.next(value);
        }

        this.storageService.setStorageItem(StorageKeyEnum.IS_ERROR_SHOW, true);
      });
    } else {
      if (error !== 'wrong_verification_code') {
        this.forceLogout.next(error);
      }
      this.storageService.setStorageItem(StorageKeyEnum.IS_ERROR_SHOW, true);
    }
  }

  isSearchMinimalSet(isMinimal: boolean): void {
    this.isSearchMinimal.next(isMinimal);
  }

  isSlidesRenderedSet(value: boolean): void {
    this.isSlidesRendered.next(value);
  }

  isMapReadySet(value: boolean): void {
    this.isMapReady.next(value);
  }

  closeMobileMenuValueSet(value: boolean): void {
    this.closeMobileMenuValue.next(value);
  }

  signInCompleteSet(value: boolean): void {
    this.signInComplete.next(value);
  }

  openYourCouponsSet(value: boolean): void {
    this.openYourCoupons.next(value);
  }

  couponPurchaseCompleteSet(value: boolean): void {
    this.couponPurchaseComplete.next(value);
  }

  ticketPurchaseCompleteSet(value: boolean): void {
    this.ticketPurchaseComplete$.next(value);
  }

  setOpenLogin(value: boolean): void {
    this.openLogin$.next(value);
  }

  setIsLoading(value: boolean): void {
    this.isLoading.next(value);
  }

  setIsFirstLoading(value: boolean): void {
    this.isFirstLoading.next(value);
  }

  setMapType(value: IMapTypeModel): void {
    this.mapType.next(value);
  }

  setUserDoneSet(value: boolean): void {
    this.setUserDone$.next(value);
  }

  setHideMap(value: boolean): void {
    this.hideMap$.next(value);
  }

  setShowSignUpDialog(value: boolean): void {
    this.showSignUpDialog$.next(value);
  }

  setLoginInProgress(value: boolean): void {
    this.loginInProgress$.next(value);
  }

  setRouteMapState(value: boolean): void {
    if (value) {
      this.router.navigate([this.router.url.split('?')[0].replace('#map', '')]);
      return;
    }
    this.router.navigate([this.router.url.split('?')[0]], {
      fragment: 'map',
      queryParams: null,
    });
  }
}

export interface ITicketFormValue {
  ticket: TicketModel;
  count: number;
}
